import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import Spinner from "../components/Spinner";
import {
  PRODUCT_CONDITIONS,
  ALLOWED_CURRENCIES,
  DIMENSION_UNIT,
  WEIGHT_UNIT,
} from "../constants/productAddingConstants";

import { emptyFormForProductRespose, createEmptyVariant } from "./commonForms";
import ImageUploader from "../components/add-product/ImageUploader";

const API_BASE = process.env.REACT_APP_PRODUCT_SERVICE_URL;
const DRAFT_KEY = "add_product_draft";

/* =========================
   Small Reusable UI Helpers
========================= */
const Required = () => <span className="text-danger">*</span>;

export default function AddProduct() {
  const { auth } = useAuth();
  const sellerId = auth?.userId;

  const [attributesMeta, setAttributesMeta] = useState({
    productAttributes: [],
    variantAttributes: [],
  });
  const [productAttributesValues, setProductAttributesValues] = useState({});

  const [variants, setVariants] = useState([createEmptyVariant()]);
  const [form, setForm] = useState(emptyFormForProductRespose);

  const [categories, setCategories] = useState([]);

  const [subCategories, setSubCategories] = useState([]);

  const [touched, setTouched] = useState({});

  const [step, setStep] = useState(1);

  const [productImages, setProductImages] = useState([]);
  const [imageError, setImageError] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });

  const step3Fields = [
    {
      name: "productName",
      label: "Product Name",
      type: "text",
      required: true,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: true,
    },
    {
      name: "brand",
      label: "Brand",
      type: "text",
      required: false,
    },
    {
      name: "modelNumber",
      label: "Model Number",
      type: "text",
      required: false,
    },
    {
      name: "productCondition",
      label: "Condition",
      type: "select",
      required: true,
      options: PRODUCT_CONDITIONS,
    },
    {
      name: "currency",
      label: "Currency",
      type: "select",
      required: true,
      options: ALLOWED_CURRENCIES,
    },

    {
      name: "dimensionUnit",
      label: "Dimension Unit",
      type: "select",
      required: true,
      options: DIMENSION_UNIT,
    },
    {
      name: "weightUnit",
      label: "Weight Unit",
      type: "select",
      required: true,
      options: WEIGHT_UNIT,
    },
    {
      name: "returnPolicy",
      label: "Return Policy",
      required: false,
      type: "text",
    },
    {
      name: "warrantyPeriod",
      label: "Warranty Period in months",
      required: false,
      type: "number",
    },
  ];

  /* =========================
     Fetch Categories
  ========================== */
  useEffect(() => {
    if (!API_BASE) return;
    fetch(`${API_BASE}/products/categories?page=0&size=100`)
      .then((res) => res.json())
      .then((res) => setCategories(res.content || []))
      .catch(() =>
        setStatus({ type: "error", message: "Failed to load categories." }),
      );
  }, []);

  /* =========================
     Fetch Subcategories
  ========================== */
  const handleCategoryChange = (e) => {
    const selectedId = Number(e.target.value);

    setForm((prev) => ({
      ...prev,
      categoryId: selectedId,
      subCategoryId: null, // reset subcategory
    }));

    fetch(`${API_BASE}/products/subcategories/${selectedId}`)
      .then((res) => res.json())
      .then((res) => {
        if (Array.isArray(res)) {
          setSubCategories(res);
        } else if (Array.isArray(res.content)) {
          setSubCategories(res.content);
        } else {
          setSubCategories([]);
        }
      })
      .catch(() =>
        setStatus({ type: "error", message: "Failed to load subcategories." }),
      );
  };

  const handleSubcategoryChange = (e) => {
    const selectedSubId = Number(e.target.value);

    setForm((prev) => ({
      ...prev,
      subCategoryId: selectedSubId,
    }));
  };

  // Fetching product attributes based on subcategory
  useEffect(() => {
    if (!form.subCategoryId) return;

    fetch(`${API_BASE}/products/subcategories/${form.subCategoryId}/attributes`)
      .then((res) => res.json())
      .then((data) => {
        setAttributesMeta(data);
      })

      .catch(() =>
        setStatus({ type: "error", message: "Failed to load attributes." }),
      );
  }, [form.subCategoryId]);

  /* =========================
     Image Preview + validation
  ========================== */

  useEffect(() => {
    return () => {
      variants.forEach((v) =>
        (v.previews || []).forEach((p) => URL.revokeObjectURL(p)),
      );
    };
  }, []);

  /* =========================
     Validation
  ========================== */
  const requiredFields = {
    1: ["categoryId"],
    2: ["subCategoryId"],
    3: step3Fields.filter((f) => f.required).map((f) => f.name),
  };

  const markStepTouched = (s) => {
    setTouched((prev) => {
      const next = { ...prev };

      (requiredFields[s] || []).forEach((f) => {
        next[f] = true;
      });

      // ALSO mark step3Fields dynamically
      if (s === 3) {
        step3Fields.forEach((f) => {
          next[f.name] = true;
        });
      }

      return next;
    });
  };

  const StepIndicator = () => (
    <div className="mb-4">
      <div className="d-flex justify-content-between">
        {["Category", "Subcategory", "Details"].map((label, index) => {
          const s = index + 1;
          return (
            <div
              key={s}
              className={`text-center flex-fill ${
                step === s
                  ? "fw-bold text-primary"
                  : step > s
                    ? "text-success"
                    : "text-muted"
              }`}
            >
              <div
                className={`rounded-circle mx-auto mb-1 ${
                  step >= s ? "bg-primary text-white" : "bg-light"
                }`}
                style={{
                  width: 32,
                  height: 32,
                  lineHeight: "32px",
                }}
              >
                {s}
              </div>
              <small>{label}</small>
            </div>
          );
        })}
      </div>
      <hr />
    </div>
  );
  /* =========================
     Reset After Success
  ========================== */
  const resetAll = () => {
    localStorage.removeItem(DRAFT_KEY);
    setStep(1);
    setForm(emptyFormForProductRespose);
    setTouched({});
    setProductImages([]);
  };

  const uploadImages = async (files, ownerId) => {
    if (!files || files.length === 0) return [];

    const formData = new FormData();
    files.forEach((file) => formData.append("imageFiles", file));
    formData.append("ownerId", ownerId);

    const res = await fetch(`${API_BASE}/products/upload/images`, {
      method: "POST",
      body: formData,
    });

    if (!res.ok) throw new Error("Image upload failed");

    return await res.json(); // [{id, url}]
  };

  /* =========================
   IMAGE VALIDATION (REUSABLE)
========================= */
  const validateImages = (files, maxSizeMB = 1) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = maxSizeMB * 1024 * 1024;

    const valid = [];
    const errors = [];

    files.forEach((file) => {
      if (!allowedTypes.includes(file.type)) {
        errors.push(`${file.name} has invalid format`);
        return;
      }
      if (file.size > maxSize) {
        errors.push(`${file.name} exceeds ${maxSizeMB}MB`);
        return;
      }
      valid.push(file);
    });

    return { valid, errors };
  };

  /* =========================
   PRODUCT IMAGES
========================= */
  const handleProductImages = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const { valid, errors } = validateImages(files);

    if (errors.length) {
      setImageError(errors[0]);
      return;
    }

    setImageError("");
    setProductImages((prev) => [...prev, ...valid]);
  };

  /* =========================
   GENERIC VARIANT UPDATER
========================= */
  const updateVariant = (index, updater) => {
    setVariants((prev) => prev.map((v, i) => (i === index ? updater(v) : v)));
  };

  /* =========================
   VARIANT FIELD CHANGE
========================= */
  const handleVariantChange = (index, field, value) => {
    updateVariant(index, (v) => ({
      ...v,
      [field]: value,
    }));
  };

  /* =========================
   VARIANT ATTRIBUTE CHANGE
========================= */
  const handleVariantAttributeChange = (index, code, value) => {
    updateVariant(index, (v) => ({
      ...v,
      attributes: {
        ...v.attributes,
        [code]: value,
      },
    }));
  };

  /* =========================
   VARIANT IMAGES
========================= */
  const handleVariantImages = (e, index) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const { valid } = validateImages(files);
    const previews = valid.map((f) => URL.createObjectURL(f));

    updateVariant(index, (v) => {
      const max = 5;

      const images = [...(v.images || []), ...valid].slice(0, max);
      const previewList = [...(v.previews || []), ...previews].slice(0, max);

      return {
        ...v,
        images,
        previews: previewList,
      };
    });
  };

  /* =========================
   REMOVE VARIANT IMAGE
========================= */
  const removeVariantImage = (variantIndex, imageIndex) => {
    updateVariant(variantIndex, (v) => ({
      ...v,
      images: v.images.filter((_, i) => i !== imageIndex),
      previews: v.previews.filter((_, i) => i !== imageIndex),
    }));
  };

  /* =========================
   ADD / REMOVE VARIANT
========================= */
  const addVariant = () => {
    setVariants((prev) => [...prev, createEmptyVariant()]);
  };

  const removeVariant = (index) => {
    setVariants((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index),
    );
  };

  /* =========================
   FORM CHANGE (FIXED)
========================= */
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    let finalValue;

    if (type === "checkbox") {
      finalValue = checked;
    } else if (type === "number") {
      finalValue = value === "" ? "" : Math.max(0, Number(value));
    } else {
      finalValue = value;
    }

    setForm((prev) => ({
      ...prev,
      [name]: finalValue,
    }));

    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));
  };

  /* =========================
   VALIDATION (ENTERPRISE)
========================= */
  const isStepValid = (s) => {
    const fields =
      s === 3
        ? step3Fields.filter((f) => f.required).map((f) => f.name)
        : requiredFields[s] || [];

    const requiredValid = fields.every((f) => {
      const value = form[f];
      return (
        value !== null && value !== undefined && String(value).trim() !== ""
      );
    });

    if (!requiredValid) return false;

    if (s === 3) {
      if (!variants.length) return false;

      return variants.every((v) => {
        return (
          v.sku?.trim() &&
          !isNaN(Number(v.price)) &&
          Number(v.price) >= 0 &&
          !isNaN(Number(v.availableQuantity)) &&
          Number(v.availableQuantity) >= 0
        );
      });
    }

    return true;
  };

  /* =========================
   DRAFT RESTORE
========================= */
  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (!draft) return;

    const parsed = JSON.parse(draft);

    setForm(parsed.form || emptyFormForProductRespose);
    setStep(parsed.step || 1);
    setVariants(parsed.variants || [createEmptyVariant()]);
    setProductAttributesValues(parsed.productAttributesValues || {});
  }, []);

  /* =========================
   DRAFT AUTO SAVE
========================= */
  useEffect(() => {
    localStorage.setItem(
      DRAFT_KEY,
      JSON.stringify({
        form,
        step,
        variants,
        productAttributesValues,
      }),
    );
  }, [form, step, variants, productAttributesValues]);

  const handleSubmit = async () => {
    if (!productImages.length) {
      setStatus({
        type: "error",
        message: "At least one product image is required",
      });
      return;
    }

    if (!isStepValid(3)) {
      markStepTouched(3);
      setStatus({
        type: "error",
        message: "Please complete all required fields.",
      });
      return;
    }

    setSubmitting(true);
    setStatus({ type: null, message: "" });

    try {
      // 1. Upload product images
      const productImages = await uploadProductImages();

      // 2. Upload variant images
      const updatedVariants = await uploadVariantImages();

      // 3. Build payload
      const payload = buildPayload({
        form,
        sellerId,
        productImages,
        variants: updatedVariants,
        attributesMeta,
        productAttributesValues,
      });

      // 4. API call
      const res = await fetch(`${API_BASE}/products/product/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Product creation failed");

      setStatus({
        type: "success",
        message: "Product created successfully.",
      });
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }, 100);

      setTimeout(() => {
        resetAll();
        setStatus({ type: null, message: "" });
      }, 1500);
    } catch (err) {
      setStatus({
        type: "error",
        message: err.message || "Something went wrong",
      });
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const uploadProductImages = async () => {
    const uploaded = await uploadImages(productImages, sellerId);

    return {
      imageIds: uploaded.map((img) => img.id),
      mainImageId: uploaded.length > 0 ? uploaded[0].id : null, // first = main
    };
  };
  const uploadVariantImages = async () => {
    return Promise.all(
      variants.map(async (v) => {
        const uploaded = await uploadImages(v.images, sellerId);

        return {
          ...v,
          imageIds: uploaded.map((img) => img.id),
        };
      }),
    );
  };

  const buildPayload = ({
    form,
    sellerId,
    productImages,
    variants,
    attributesMeta,
    productAttributesValues,
  }) => {
    const hasVariants = variants.length > 0;

    const mapAttributes = (attrs) => {
      return Object.entries(attrs || {}).map(([code, value]) => {
        const allAttributes = [
          ...attributesMeta.productAttributes,
          ...attributesMeta.variantAttributes,
        ];

        const meta = allAttributes.find((a) => a.code === code);

        if (!meta) {
          throw new Error(`Attribute metadata not found for ${code}`);
        }

        if (meta.options?.length > 0) {
          const option = meta.options.find((opt) => opt.id === Number(value));

          if (!option) {
            throw new Error(`Invalid value "${value}" for ${code}`);
          }

          return {
            attributeCode: code,
            optionId: option.id,
            valueText: null,
            valueNumber: null,
            valueBoolean: null,
          };
        }
        const type = meta.type || meta.inputType;
        if (type === "NUMBER") {
          return {
            attributeCode: code,
            optionId: null,
            valueText: null,
            valueNumber: value !== "" ? Number(value) : null,
            valueBoolean: null,
          };
        }

        if (type === "BOOLEAN") {
          return {
            attributeCode: code,
            optionId: null,
            valueText: null,
            valueNumber: null,
            valueBoolean: Boolean(value),
          };
        }

        return {
          attributeCode: code,
          optionId: null,
          valueText: value ? String(value) : null,
          valueNumber: null,
          valueBoolean: null,
        };
      });
    };

    return {
      // =========================
      // BASIC
      // =========================
      sellerId,
      productName: form.productName,
      description: form.description,
      brand: form.brand || null,
      modelNumber: form.modelNumber || null,

      dimensionUnit: form.dimensionUnit,
      weightUnit: form.weightUnit,
      returnPolicy: form.returnPolicy,
      warrantyPeriod:
        form.warrantyPeriod !== "" ? String(form.warrantyPeriod) : null,

      costPrice: form.costPrice ? Number(form.costPrice) : null,
      currency: form.currency,
      taxRate: form.taxRate ? Number(form.taxRate) : null,

      productCondition: form.productCondition,

      categoryId: Number(form.categoryId),
      subCategoryId: Number(form.subCategoryId),

      tags: [],

      // =========================
      // PRODUCT ATTRIBUTES
      // =========================
      attributeRequests: mapAttributes(productAttributesValues),

      // =========================
      // IMAGES
      // =========================
      imageIds: productImages.imageIds,
      mainImageId: productImages.mainImageId,

      // =========================
      // FALLBACK (NO VARIANTS)
      // =========================
      sku: !hasVariants ? form.sku : null,
      price: !hasVariants ? Number(form.price) : null,
      availableQuantity: !hasVariants ? Number(form.availableQuantity) : null,

      weight: !hasVariants ? Number(form.weight || 0) : null,
      width: !hasVariants ? Number(form.width || 0) : null,
      length: !hasVariants ? Number(form.length || 0) : null,
      height: !hasVariants ? Number(form.height || 0) : null,

      // =========================
      // VARIANTS
      // =========================
      variantRequests: hasVariants
        ? variants.map((v, index) => ({
            sku: v.sku,
            price: v.price !== "" ? Number(v.price) : null,
            availableQuantity:
              v.availableQuantity !== "" ? Number(v.availableQuantity) : null,

            weight: Number(v.weight || 0),
            width: Number(v.width || 0),
            height: Number(v.height || 0),
            length: Number(v.length || 0),

            isDefault: index === 0,

            attributeRequests: mapAttributes(v.attributes),

            imageIds: v.imageIds || [],
          }))
        : [],
    };
  };

  /* =========================
     UI
  ========================== */
  return (
    <div className="container mt-4 mb-5">
      <div className="card shadow border-0">
        <div className="card-body p-4">
          <h1>Add Product Form</h1>
          <StepIndicator />

          {/* STEP 1 */}
          {step === 1 && (
            <>
              <label className="form-label">
                Category <Required />
              </label>
              <select
                className={`form-select ${
                  touched.categoryId && !form.categoryId ? "is-invalid" : ""
                }`}
                name="categoryId"
                value={form.categoryId}
                onChange={handleCategoryChange}
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (!isStepValid(1)) return markStepTouched(1);
                  setStep(2);
                }}
              >
                Next
              </button>
            </>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              <label className="form-label">
                Subcategory <Required />
              </label>
              <select
                className={`form-select ${
                  touched.subCategoryId && !form.subCategoryId
                    ? "is-invalid"
                    : ""
                }`}
                name="subCategoryId"
                value={form.subCategoryId}
                onChange={handleSubcategoryChange}
              >
                <option value="">Select Subcategory</option>
                {subCategories.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name} ({sub.description})
                  </option>
                ))}
              </select>

              <div className="d-flex justify-content-between mt-3">
                <button
                  className="btn btn-secondary"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>

                <button
                  className="btn btn-primary"
                  onClick={() => {
                    if (!isStepValid(2)) return markStepTouched(2);
                    setStep(3);
                  }}
                >
                  Next
                </button>
              </div>
            </>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <>
              {/* ================= BASIC INFO ================= */}
              <div className="card mb-4 shadow-sm border-0">
                <div className="card-body">
                  <h5 className="fw-bold mb-3">Basic Information</h5>

                  <div className="row">
                    {step3Fields.map((field) => {
                      const isInvalid =
                        touched[field.name] &&
                        (form[field.name] === null ||
                          form[field.name] === undefined ||
                          String(form[field.name]).trim() === "");

                      return (
                        <div key={field.name} className="col-md-6 mb-3">
                          <label className="form-label">
                            {field.label} {field.required && <Required />}
                          </label>

                          {field.type === "textarea" ? (
                            <textarea
                              name={field.name}
                              value={form[field.name]}
                              onChange={handleChange}
                              className={`form-control ${isInvalid ? "is-invalid" : ""}`}
                            />
                          ) : field.type === "select" ? (
                            <select
                              name={field.name}
                              value={form[field.name]}
                              onChange={handleChange}
                              className={`form-control ${isInvalid ? "is-invalid" : ""}`}
                            >
                              {field.options.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              type={field.type}
                              name={field.name}
                              value={form[field.name]}
                              onChange={handleChange}
                              className={`form-control ${isInvalid ? "is-invalid" : ""}`}
                            />
                          )}

                          {isInvalid && (
                            <div className="invalid-feedback">
                              This field is required
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* ================= PRODUCT ATTRIBUTES ================= */}
              <div className="card mb-4 shadow-sm border-0">
                <div className="card-body">
                  <h5 className="fw-bold mb-3">Product Attributes</h5>

                  <div className="row">
                    {attributesMeta.productAttributes.map((attr) => (
                      <div key={attr.id} className="col-md-6 mb-3">
                        <label className="form-label">
                          {attr.name} {attr.required && <Required />}
                          {attr.unit && ` (${attr.unit})`}
                        </label>

                        {attr.inputType === "select" ? (
                          <select
                            className="form-select"
                            onChange={(e) =>
                              setProductAttributesValues((prev) => ({
                                ...prev,
                                [attr.code]: e.target.value,
                              }))
                            }
                          >
                            <option value="">Select</option>
                            {attr.options.map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.value}
                              </option>
                            ))}
                          </select>
                        ) : attr.inputType === "checkbox" ? (
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!productAttributesValues[attr.code]}
                            onChange={(e) =>
                              setProductAttributesValues((prev) => ({
                                ...prev,
                                [attr.code]: e.target.checked,
                              }))
                            }
                          />
                        ) : (
                          <input
                            type={attr.inputType}
                            className="form-control"
                            onChange={(e) =>
                              setProductAttributesValues((prev) => ({
                                ...prev,
                                [attr.code]: e.target.value,
                              }))
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ================= PRODUCT IMAGES ================= */}
              <div className="card mb-4 shadow-sm border-0">
                <div className="card-body">
                  <ImageUploader
                    images={productImages}
                    setImages={setProductImages}
                    error={imageError}
                    setError={setImageError}
                    maxImages={5}
                    maxSizeMB={1}
                  />
                </div>
              </div>

              {/* ================= VARIANTS ================= */}
              <div className="card mb-4 shadow-sm border-0">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="fw-bold">Variants</h5>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={() =>
                        setVariants((prev) => [...prev, createEmptyVariant()])
                      }
                    >
                      + Add Variant
                    </button>
                  </div>

                  {variants.map((variant, index) => (
                    <div
                      key={index}
                      className="border rounded p-3 mb-3 bg-light"
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <h6>
                          Variant {index + 1}
                          {index === 0 && (
                            <span className="badge bg-success ms-2">
                              Default
                            </span>
                          )}
                        </h6>

                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          disabled={variants.length === 1}
                          onClick={() =>
                            setVariants((prev) =>
                              prev.filter((_, i) => i !== index),
                            )
                          }
                        >
                          Remove
                        </button>
                      </div>

                      {/* BASIC FIELDS */}
                      <div className="row mt-3">
                        <div className="col-md-4 mb-2">
                          <input
                            placeholder="SKU"
                            className="form-control"
                            value={variant.sku}
                            onChange={(e) =>
                              setVariants((prev) =>
                                prev.map((v, i) =>
                                  i === index
                                    ? { ...v, sku: e.target.value }
                                    : v,
                                ),
                              )
                            }
                          />
                        </div>

                        <div className="col-md-4 mb-2">
                          <input
                            type="number"
                            placeholder="Price"
                            className="form-control"
                            value={variant.price}
                            onChange={(e) =>
                              setVariants((prev) =>
                                prev.map((v, i) =>
                                  i === index
                                    ? { ...v, price: e.target.value }
                                    : v,
                                ),
                              )
                            }
                          />
                        </div>

                        <div className="col-md-4 mb-2">
                          <input
                            placeholder="Quantity"
                            className="form-control"
                            value={variant.availableQuantity}
                            onChange={(e) =>
                              setVariants((prev) =>
                                prev.map((v, i) =>
                                  i === index
                                    ? {
                                        ...v,
                                        availableQuantity: e.target.value,
                                      }
                                    : v,
                                ),
                              )
                            }
                          />
                        </div>
                      </div>

                      {/* ATTRIBUTES */}
                      <div className="row">
                        {attributesMeta.variantAttributes.map((attr) => (
                          <div key={attr.id} className="col-md-4 mb-2">
                            <label className="form-label small">
                              {attr.name}
                              {attr.unit && ` (${attr.unit})`}
                            </label>
                            <select
                              className="form-select"
                              onChange={(e) =>
                                setVariants((prev) =>
                                  prev.map((v, i) =>
                                    i === index
                                      ? {
                                          ...v,
                                          attributes: {
                                            ...v.attributes,
                                            [attr.code]: e.target.value,
                                          },
                                        }
                                      : v,
                                  ),
                                )
                              }
                            >
                              <option value="">Select</option>
                              {attr.options.map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.value}
                                </option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>

                      {/* ================= VARIANT IMAGES ================= */}
                      <div className="mt-3">
                        <ImageUploader
                          label={`Variant ${index + 1} Images`}
                          images={variant.images || []}
                          maxImages={5}
                          maxSizeMB={1}
                          error={variant.imageError || ""}
                          setError={(msg) =>
                            setVariants((prev) =>
                              prev.map((v, i) =>
                                i === index ? { ...v, imageError: msg } : v,
                              ),
                            )
                          }
                          setImages={(updater) =>
                            setVariants((prev) =>
                              prev.map((v, i) =>
                                i === index
                                  ? {
                                      ...v,
                                      images:
                                        typeof updater === "function"
                                          ? updater(v.images || [])
                                          : updater,
                                    }
                                  : v,
                              ),
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ================= STATUS MESSAGE ================= */}
              {status.type && (
                <div
                  className={`alert mt-4${
                    status.type === "success" ? "alert-success" : "alert-danger"
                  }`}
                >
                  {status.message}
                </div>
              )}

              {/* ================= ACTIONS ================= */}
              <div className="d-flex justify-content-between mt-3 sticky-bottom bg-white p-3 border-top">
                <button
                  className="btn btn-secondary"
                  onClick={() => setStep(2)}
                >
                  Back
                </button>

                <button
                  className="btn btn-success px-4"
                  disabled={submitting || !isStepValid(3)}
                  onClick={handleSubmit}
                >
                  {submitting ? (
                    <span className="d-flex align-items-center gap-2">
                      <Spinner text="Creating" /> Creating...
                    </span>
                  ) : (
                    "Create Product"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
